import { useCallback, useEffect, useMemo } from 'react';
import useFetchComments from './useFetchComments';
import useCollaborationSidebarState from './useCollaborationSidebarState';
import { createModuleRepliesMap } from '../utils/commentingUtils';
import { useRealtimeCommentingMessaging } from '../features/messaging/messageHandlers';
import useHasCommentingFeature from './useHasCommentingFeature';
import { trackInteraction } from '../utils/usageTracker';
export const useRealtimeCommentHandler = ({
  objectType,
  objectId,
  callback,
  skip
}) => {
  useRealtimeCommentingMessaging({
    skip,
    onReportNewComment: msg => {
      if (msg.payload.objectType !== objectType || msg.payload.objectId !== objectId) {
        return;
      }
      callback();
    }
  });
};
export default function useHasUnreadComments(objectType, objectId) {
  'use no memo';

  const {
    fetchComments,
    comments,
    updateCache: updateCommentsCache
  } = useFetchComments();
  const hasCommentingFeature = useHasCommentingFeature();
  const {
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();
  const fetchCommentsCallback = useCallback(() => {
    fetchComments({
      objectType,
      objectId,
      refetch: true
    });
  }, [fetchComments, objectId, objectType]);
  useEffect(() => {
    fetchCommentsCallback();
  }, [fetchCommentsCallback]);
  useRealtimeCommentHandler({
    objectType,
    objectId,
    callback: () => {
      fetchCommentsCallback();
      trackInteraction({
        action: 'New comments while viewing asset'
      });
    },
    skip: !hasCommentingFeature
  });
  const unreadComments = useMemo(() => comments.filter(comment => !comment.read), [comments]);
  const unreadCommentsCount = useMemo(() => unreadComments.length, [unreadComments]);
  const hasUnreadComments = unreadCommentsCount > 0;
  const markCommentAsRead = useCallback(updatedCommentId => {
    if (!objectId || !objectType) {
      return;
    }
    updateCommentsCache({
      objectType,
      objectId,
      updatedCommentId
    });
  }, [objectId, objectType, updateCommentsCache]);
  useEffect(() => {
    onUpdateCollaborationSidebarState({
      unreadRepliesMap: createModuleRepliesMap(unreadComments)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadComments]);
  return {
    hasUnreadComments,
    unreadCommentsCount,
    refetchComments: () => fetchComments({
      objectType,
      objectId,
      refetch: true
    }),
    markCommentAsRead
  };
}